import { LogLevels } from "./app/types/Log/LogLevels";

export const handlePromise = <T>(
  promise: Promise<T>,
): Promise<[Error | undefined, T | undefined]> =>
  promise
    .then<[undefined, T]>((data) => [undefined, data])
    .catch<[Error, undefined]>((error) => [error, undefined]);

export const arrayReplace = <T>(array: T[], index: number, newItem: T): T[] => {
  const newArray = [...array];
  newArray[index] = newItem;
  return newArray;
};

/**
 * Reduce an array to a single value but with the ability to exit early based on a condition.
 * @template T The type of array elements and accumulator.
 * @param {T[]} data - The array to reduce.
 * @param {(accumulator: T, currentValue: T, currentIndex: number) => T} callback - The reducing function.
 * @param {(accumulator: T, currentValue: T, currentIndex: number) => boolean} exitCondition - The function to decide when to exit early.
 * @param {T} initialValue - The initial value to start the reduction with.
 * @returns {T} The reduced value.
 */
export const speedyReduce = <T>(
  data: T[],
  callback: (accumulator: T, currentValue: T, currentIndex: number) => T,
  exitCondition: (
    accumulator: T,
    currentValue: T,
    currentIndex: number,
  ) => boolean,
  initialValue: T,
): T => {
  return data.reduce((acc, current, index, array) => {
    const newAcc = callback(acc, current, index);
    if (exitCondition(newAcc, current, index)) {
      array.splice(0, array.length);
    }
    return newAcc;
  }, initialValue);
};

export const safeJSONParse = <T>(
  json: string,
  defaultValue: T,
  logger: any,
  logMeta?: any,
  logLevel: LogLevels = "ERROR",
): T => {
  try {
    if (!json) {
      logger.log(logLevel, "Attempting To Parse Invalid Input", {
        ...logMeta,
        inputToParse: json,
      });
      return defaultValue || ({} as T);
    }
    return JSON.parse(json);
  } catch (err) {
    logger.log(logLevel, err, { ...logMeta, inputToParse: json });
    return defaultValue || ({} as T);
  }
};
export const mergeArray = (old: any, newData: any, key: string) => {
  const oldMap = old.reduce((acc: any, item: any) => {
    return { ...acc, [item[key]]: item };
  }, {});
  const newMap = newData.reduce((acc: any, item: any) => {
    return { ...acc, [item[key]]: item };
  }, {});
  return Object.values({ ...oldMap, ...newMap });
};

export const isUUID = (str: string) => {
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
    str,
  );
};

export const handleTruthyValues = (value: any) => {
  if (
    typeof value === "string" &&
    ["1", "true", "okay", "yes", "y"].includes(value.toLowerCase())
  ) {
    return true;
  }
  if (
    typeof value === "string" &&
    ["0", "false", "no", "n", ""].includes(value.toLowerCase())
  ) {
    return false;
  }
  return value;
};

export const pluralize = (str: string, num: number): string => {
  if (num === 1) {
    return str;
  }
  return `${str}s`;
};

export const convertStringsToBooleans = (data: any): any => {
  if (Array.isArray(data)) {
    return data.map((item: any) => handleTruthyValues(item));
  }
  if (typeof data === "object") {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]: any) => {
        return [key, handleTruthyValues(value)];
      }),
    );
  }
  return data;
};

export const sleep = (ms: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};
